// Client-safe selection + locale mapping (no fs/markdown imports, so it can run
// in the browser as well as at build time).
import { getRootLanguage, type LocaleCode } from 'utils/LocaleUtils';
import {
  type LegalContentLocale,
  type LegalDocument,
  type LegalVariants,
} from './types';

// Maps a regional app locale (e.g. en-AU, fr-CA) onto the coarse legal content
// variant we author. Anything we don't author a localised legal text for
// resolves to `en` — which is the canonical document, not a silent fallback
// for a *missing requested* localisation.
export function legalLocaleFor(locale: LocaleCode): LegalContentLocale {
  switch (getRootLanguage(locale)) {
    case 'fr':
      return 'fr';
    case 'it':
      return 'it';
    case 'es':
      return 'es';
    case 'de':
      return 'de';
    default:
      return 'en';
  }
}

// Returns the EXACT variant or null. Never falls back to another locale.
export function getLegalPage(
  variants: LegalVariants,
  contentLocale: LegalContentLocale,
): LegalDocument | null {
  return variants[contentLocale] ?? null;
}

// URL prefix + hreflang for each content locale, used to build canonical and
// alternate links.
export const LEGAL_LOCALE_META: Record<
  LegalContentLocale,
  { prefix: string; hreflang: string }
> = {
  en: { prefix: '', hreflang: 'en' },
  fr: { prefix: '/fr', hreflang: 'fr' },
  it: { prefix: '/it', hreflang: 'it' },
  es: { prefix: '/es', hreflang: 'es' },
  de: { prefix: '/de', hreflang: 'de' },
};
